import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { ArrowLeft, Briefcase, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import DeleteConfirmModal from '../../components/DeleteConfirmModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProjectDetailPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [project, setProject] = useState(null);
  const [company, setCompany] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    number_of_consultants: 1,
    status: 'active'
  });

  useEffect(() => {
    fetchProjectDetail();
  }, [projectId]);

  const fetchProjectDetail = async () => {
    const token = localStorage.getItem('token');
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/projects/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProject(response.data.project);
      setCompany(response.data.company);
      setFormData({
        title: response.data.project.title || '',
        description: response.data.project.description || '',
        number_of_consultants: response.data.project.number_of_consultants || 1,
        status: response.data.project.status || 'active'
      });
    } catch (error) {
      toast.error('Failed to load project details');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const token = localStorage.getItem('token');
    setSaving(true);
    try {
      await axios.put(`${API}/admin/projects/${projectId}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Project updated successfully');
      fetchProjectDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update project');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    const token = localStorage.getItem('token');
    
    try {
      await axios.delete(`${API}/admin/projects/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { reason: 'Deleted from admin panel' }
      });
      toast.success('Project deleted successfully');
      navigate('/admin/projects');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete project');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#62D84E' }}></div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="p-8">
        <p className="text-red-600">Project not found</p>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <Button variant="ghost" onClick={() => navigate('/admin/projects')} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Projects
          </Button>
          <h1 className="text-3xl font-bold" style={{ color: '#1B3240' }}>Project Details</h1>
          <p className="text-gray-600 mt-2">View and edit project information</p>
        </div>
        <Button variant="outline" onClick={() => setShowDeleteModal(true)} className="text-red-600">
          <Trash2 className="w-4 h-4 mr-2" />
          Delete Project
        </Button>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title="Delete Project"
        message={`Are you sure you want to delete ${project.title}?`}
        entityName={project.title}
        consequences={[
          'Project will be marked as deleted',
          'Cannot delete if there are active reservations'
        ]}
        isDeleting={deleting}
      />

      {/* Edit Form */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Edit Project</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label>Title</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
              />
            </div>
            <div className="col-span-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows={4}
              />
            </div>
            <div>
              <Label>Number of Consultants</Label>
              <Input
                type="number"
                value={formData.number_of_consultants}
                onChange={(e) => setFormData({...formData, number_of_consultants: parseInt(e.target.value)})}
              />
            </div>
            <div>
              <Label>Status</Label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({...formData, status: e.target.value})}
                className="w-full border rounded-md px-3 py-2"
              >
                <option value="active">Active</option>
                <option value="on_hold">On Hold</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>
          <div className="mt-4">
            <Button onClick={handleSave} disabled={saving} style={{ backgroundColor: '#62D84E' }}>
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Project Information */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Briefcase className="w-5 h-5 mr-2" />
              Basic Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <p className="text-gray-600 text-sm">Project ID</p>
              <p className="font-medium">{project.project_id}</p>
            </div>
            <div>
              <p className="text-gray-600 text-sm">Title</p>
              <p className="font-medium">{project.title}</p>
            </div>
            <div>
              <p className="text-gray-600 text-sm">Description</p>
              <p className="font-medium">{project.description || 'N/A'}</p>
            </div>
            <div>
              <p className="text-gray-600 text-sm">Type</p>
              <p className="font-medium">{project.project_type || 'N/A'}</p>
            </div>
            <div>
              <p className="text-gray-600 text-sm">Status</p>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                project.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
              }`}>
                {project.status ? project.status.toUpperCase() : 'N/A'}
              </span>
            </div>
            <div>
              <p className="text-gray-600 text-sm">Activation Status</p>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                project.is_activated ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
              }`}>
                {project.is_activated ? 'ACTIVE' : 'INACTIVE'}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Additional Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <p className="text-gray-600 text-sm">Company</p>
              <p className="font-medium">{company?.name || 'N/A'}</p>
            </div>
            <div>
              <p className="text-gray-600 text-sm">Budget</p>
              <p className="font-medium">${project.budget?.toLocaleString() || 0}</p>
            </div>
            <div>
              <p className="text-gray-600 text-sm">Duration</p>
              <p className="font-medium">{project.duration} {project.duration_unit}</p>
            </div>
            <div>
              <p className="text-gray-600 text-sm">Location</p>
              <p className="font-medium">{project.location || 'N/A'}</p>
            </div>
            <div>
              <p className="text-gray-600 text-sm">Start Date</p>
              <p className="font-medium">{project.start_date ? new Date(project.start_date).toLocaleDateString() : 'N/A'}</p>
            </div>
            <div>
              <p className="text-gray-600 text-sm">Created At</p>
              <p className="font-medium">{new Date(project.created_at).toLocaleString()}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProjectDetailPage;